import React, { useEffect, useRef } from 'react';
import { motion, useMotionValue, useSpring, useTransform, useScroll, useReducedMotion } from 'motion/react';

/* ------------------------------------------------------------------ */
/* Tilt — mouse-driven 3D tilt with moving glare                       */
/* ------------------------------------------------------------------ */
export const Tilt = ({ children, className = '', style, max = 10, scale = 1.015, glare = true, as: Tag = motion.div, ...rest }) => {
  const reduce = useReducedMotion();
  const rx = useMotionValue(0);
  const ry = useMotionValue(0);
  const gx = useMotionValue(50);
  const gy = useMotionValue(50);
  const srx = useSpring(rx, { stiffness: 220, damping: 22, mass: 0.6 });
  const sry = useSpring(ry, { stiffness: 220, damping: 22, mass: 0.6 });
  const s = useMotionValue(1);
  const ss = useSpring(s, { stiffness: 220, damping: 24 });
  const glareBg = useTransform([gx, gy], ([x, y]) => `radial-gradient(circle at ${x}% ${y}%, rgba(255,255,255,0.18), rgba(255,255,255,0) 55%)`);

  const onMove = (e) => {
    if (reduce) return;
    const r = e.currentTarget.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width;
    const py = (e.clientY - r.top) / r.height;
    ry.set((px - 0.5) * max * 2);
    rx.set((0.5 - py) * max * 2);
    gx.set(px * 100);
    gy.set(py * 100);
    s.set(scale);
  };
  const onLeave = () => { rx.set(0); ry.set(0); s.set(1); gx.set(50); gy.set(50); };

  return (
    <Tag
      className={`tilt ${className}`}
      style={{ ...style, rotateX: srx, rotateY: sry, scale: ss, transformPerspective: 1100, transformStyle: 'preserve-3d' }}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      {...rest}
    >
      {children}
      {glare && !reduce && <motion.div className="tilt-glare" style={{ background: glareBg }} aria-hidden="true" />}
    </Tag>
  );
};

/* ------------------------------------------------------------------ */
/* ScrollTilt — perspective rotation driven by scroll position          */
/* ------------------------------------------------------------------ */
export const ScrollTilt = ({ children, className = '', style, from = 16, to = -6 }) => {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'end start'] });
  const rotateX = useTransform(scrollYProgress, [0, 0.5, 1], [from, 0, to]);
  const scale = useTransform(scrollYProgress, [0, 0.5, 1], [0.94, 1, 0.98]);
  const opacity = useTransform(scrollYProgress, [0, 0.25, 0.8, 1], [0.4, 1, 1, 0.75]);
  return (
    <div ref={ref} className={`scroll-tilt ${className}`} style={{ perspective: 1400, ...style }}>
      <motion.div style={reduce ? undefined : { rotateX, scale, opacity, transformStyle: 'preserve-3d' }}>
        {children}
      </motion.div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Globe3D — canvas point-sphere with great-circle arcs from Baijnath   */
/* ------------------------------------------------------------------ */
const HOME = { lat: 32.05, lon: 76.65, label: 'Baijnath, HP' };
const DESTS = [
  { lat: 41.82, lon: -71.41, label: 'Rhode Island' },
  { lat: 59.33, lon: 18.07, label: 'Stockholm' },
  { lat: 21.17, lon: 72.83, label: 'Surat' },
  { lat: 30.73, lon: 76.78, label: 'Chandigarh' },
  { lat: 12.97, lon: 77.59, label: 'Bengaluru' }
];

const toVec = (lat, lon) => {
  const phi = (90 - lat) * Math.PI / 180;
  const theta = (lon + 180) * Math.PI / 180;
  return [-Math.sin(phi) * Math.cos(theta), Math.cos(phi), Math.sin(phi) * Math.sin(theta)];
};

const slerp = (a, b, t) => {
  const dot = Math.max(-1, Math.min(1, a[0] * b[0] + a[1] * b[1] + a[2] * b[2]));
  const om = Math.acos(dot);
  if (om < 1e-4) return a;
  const s1 = Math.sin((1 - t) * om) / Math.sin(om);
  const s2 = Math.sin(t * om) / Math.sin(om);
  return [a[0] * s1 + b[0] * s2, a[1] * s1 + b[1] * s2, a[2] * s1 + b[2] * s2];
};

export const Globe3D = ({ size = 520, className = '', style, accent = '#22D3EE', accent2 = '#A78BFA' }) => {
  const ref = useRef(null);
  const reduce = useReducedMotion();

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext('2d');
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = size * dpr;
    canvas.height = size * dpr;
    ctx.scale(dpr, dpr);

    const N = 900;
    const pts = [];
    const golden = Math.PI * (3 - Math.sqrt(5));
    for (let i = 0; i < N; i++) {
      const y = 1 - (i / (N - 1)) * 2;
      const r = Math.sqrt(1 - y * y);
      const th = golden * i;
      pts.push([Math.cos(th) * r, y, Math.sin(th) * r]);
    }
    const home = toVec(HOME.lat, HOME.lon);
    const arcs = DESTS.map((d) => ({ to: toVec(d.lat, d.lon), label: d.label }));

    const R = size * 0.36;
    const cx = size / 2;
    const cy = size / 2;
    let rot = 0.6;
    let raf = 0;
    let running = true;
    let t0 = performance.now();

    const rotate = ([x, y, z], a, tiltX = -0.35) => {
      const x1 = x * Math.cos(a) + z * Math.sin(a);
      const z1 = -x * Math.sin(a) + z * Math.cos(a);
      const y2 = y * Math.cos(tiltX) - z1 * Math.sin(tiltX);
      const z2 = y * Math.sin(tiltX) + z1 * Math.cos(tiltX);
      return [x1, y2, z2];
    };
    const project = ([x, y, z]) => {
      const f = 2.2 / (2.2 - z * 0.9);
      return [cx + x * R * f, cy - y * R * f, z, f];
    };

    const draw = (now) => {
      if (!running) return;
      const dt = Math.min(0.05, (now - t0) / 1000);
      t0 = now;
      if (!reduce) rot += dt * 0.18;
      ctx.clearRect(0, 0, size, size);

      // atmosphere
      const g = ctx.createRadialGradient(cx, cy, R * 0.6, cx, cy, R * 1.25);
      g.addColorStop(0, 'rgba(34,211,238,0.10)');
      g.addColorStop(1, 'rgba(34,211,238,0)');
      ctx.fillStyle = g;
      ctx.beginPath(); ctx.arc(cx, cy, R * 1.25, 0, Math.PI * 2); ctx.fill();

      // points
      for (const p of pts) {
        const q = rotate(p, rot);
        const [sx, sy, z] = project(q);
        if (z < -0.15) continue;
        const a = 0.15 + (z + 0.15) * 0.6;
        ctx.fillStyle = `rgba(167,196,255,${a.toFixed(3)})`;
        ctx.beginPath(); ctx.arc(sx, sy, 0.9 + (z + 1) * 0.5, 0, Math.PI * 2); ctx.fill();
      }

      // arcs with travelling pulse
      const phase = (now / 1000) % 3;
      arcs.forEach((arc, i) => {
        const steps = 48;
        ctx.beginPath();
        let started = false;
        for (let s = 0; s <= steps; s++) {
          const t = s / steps;
          const v = slerp(home, arc.to, t);
          const lift = 1 + Math.sin(t * Math.PI) * 0.22;
          const q = rotate([v[0] * lift, v[1] * lift, v[2] * lift], rot);
          const [sx, sy, z] = project(q);
          if (z < -0.1) { started = false; continue; }
          if (!started) { ctx.moveTo(sx, sy); started = true; } else ctx.lineTo(sx, sy);
        }
        ctx.strokeStyle = i % 2 ? `${accent2}99` : `${accent}99`;
        ctx.lineWidth = 1.1;
        ctx.stroke();

        const pt = ((phase + i * 0.6) % 3) / 3;
        const v = slerp(home, arc.to, pt);
        const lift = 1 + Math.sin(pt * Math.PI) * 0.22;
        const q = rotate([v[0] * lift, v[1] * lift, v[2] * lift], rot);
        const [sx, sy, z] = project(q);
        if (z > -0.1) {
          ctx.fillStyle = i % 2 ? accent2 : accent;
          ctx.shadowColor = ctx.fillStyle; ctx.shadowBlur = 10;
          ctx.beginPath(); ctx.arc(sx, sy, 2.2, 0, Math.PI * 2); ctx.fill();
          ctx.shadowBlur = 0;
        }

        // destination marker
        const [dx, dy, dz] = project(rotate(arc.to, rot));
        if (dz > -0.05) {
          ctx.fillStyle = 'rgba(255,255,255,0.9)';
          ctx.beginPath(); ctx.arc(dx, dy, 2, 0, Math.PI * 2); ctx.fill();
        }
      });

      // home marker
      const [hx, hy, hz] = project(rotate(home, rot));
      if (hz > -0.05) {
        const pulse = 4 + Math.sin(now / 300) * 2;
        ctx.strokeStyle = `${accent}AA`; ctx.lineWidth = 1;
        ctx.beginPath(); ctx.arc(hx, hy, pulse + 4, 0, Math.PI * 2); ctx.stroke();
        ctx.fillStyle = accent; ctx.shadowColor = accent; ctx.shadowBlur = 14;
        ctx.beginPath(); ctx.arc(hx, hy, 3.2, 0, Math.PI * 2); ctx.fill();
        ctx.shadowBlur = 0;
      }

      raf = requestAnimationFrame(draw);
    };

    const io = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) { if (!running) { running = true; t0 = performance.now(); raf = requestAnimationFrame(draw); } }
      else { running = false; cancelAnimationFrame(raf); }
    });
    io.observe(canvas);
    raf = requestAnimationFrame(draw);
    return () => { running = false; cancelAnimationFrame(raf); io.disconnect(); };
  }, [size, reduce, accent, accent2]);

  return <canvas ref={ref} className={`globe3d ${className}`} style={{ width: size, height: size, ...style }} aria-hidden="true" />;
};

/* ------------------------------------------------------------------ */
/* TechCube — CSS 3D cube with logo faces                               */
/* ------------------------------------------------------------------ */
export const TechCube = ({ items = [], size = 220, className = '' }) => {
  const faces = ['front', 'back', 'right', 'left', 'top', 'bottom'];
  const perFace = Math.max(1, Math.ceil(items.length / 6));
  return (
    <div className={`cube-scene ${className}`} style={{ '--cube': `${size}px` }}>
      <div className="cube">
        {faces.map((f, i) => (
          <div key={f} className={`cube-face cube-${f}`}>
            {items.slice(i * perFace, i * perFace + perFace).map((it) => (
              <div key={it.name} className="cube-logo" title={it.name}>
                {it.logo ? <img src={it.logo} alt={it.name} loading="lazy" /> : <span>{it.name.slice(0, 2).toUpperCase()}</span>}
              </div>
            ))}
          </div>
        ))}
      </div>
      <div className="cube-shadow" />
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* PageTransition — 3D swing between routes                             */
/* ------------------------------------------------------------------ */
export const pageVariants = {
  initial: { opacity: 0, y: 24, rotateX: 6, transformPerspective: 1400 },
  animate: { opacity: 1, y: 0, rotateX: 0, transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] } },
  exit: { opacity: 0, y: -12, rotateX: -4, transition: { duration: 0.28, ease: 'easeIn' } }
};

/* ------------------------------------------------------------------ */
/* Orbit — decorative 3D rings                                          */
/* ------------------------------------------------------------------ */
export const Orbit = ({ size = 640, className = '' }) => (
  <div className={`orbit ${className}`} style={{ '--orbit': `${size}px` }} aria-hidden="true">
    <div className="orbit-ring r1" /><div className="orbit-ring r2" /><div className="orbit-ring r3" />
  </div>
);
